
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Icons } from '@shared/constants';
import { SocialAuthButtons } from './SocialAuthButtons';
import emailSpellChecker from '@zootools/email-spell-checker';
import { trackEvent } from '@shared/services/analyticsTracker';
import { logError } from '@/lib/log';
import { FUNNEL_COLUMN } from '@/lib/funnelLayout';

/**
 * Account creation / sign-in — the step the pre-login funnel hands off to.
 *
 * VISUAL CONTRACT (2026-07-21): this screen is part of the funnel, not a
 * destination the funnel dumps you at, so it wears the same dark treatment as
 * the value-onboarding carousel: `bg-growth` with the two soft
 * blurred blobs, the beige wordmark, sand-on-green type, and the foliage pill
 * CTA. It previously rendered a white card on `bg-sand`, which read as a
 * different product the moment the carousel handed over.
 *
 * What is deliberately NOT restyled: `SocialAuthButtons`. Google (white +
 * #747775 hairline) and Apple (black + white mark) buttons are vendor-mandated
 * and must override the app palette — they already sit correctly on dark.
 *
 * DO NOT change without checking `e2e/auth/auth-flow.spec.ts` and
 * `e2e/features/fixtures.ts`: the suites drive this screen by exact button
 * strings ("Sign In" exact, "Create Account", "Already have an account? Sign
 * In", "Back to Sign In") and by `input#email` / `input#password`. The auth
 * ORCHESTRATION — anon→uid preservation, per-provider social quirks, the
 * user-not-found→signup fallback — lives in AuthContext and is untouched here.
 */

interface AuthProps {
    defaultMode?: 'login' | 'signup';
    /** The visitor arrived from a completed pre-login purchase (postPurchase=1). */
    postPurchase?: boolean;
}

/** Funnel field styling — dark, low-contrast fill with a foliage focus edge. */
const FIELD_CLASS =
    'w-full rounded-xl border border-sand/20 bg-sand/10 px-4 py-3 text-[15px] text-sand ' +
    'placeholder:text-sand/35 transition-colors focus:border-foliage/70 focus:outline-hidden';

const LABEL_CLASS = 'mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-sand/60';

/** The carousel's CTA, verbatim — same pill, same lift, same glow. */
const PRIMARY_CTA_CLASS =
    'w-full rounded-full bg-foliage py-4 text-[15px] font-medium text-growth ' +
    'shadow-[0_10px_30px_rgba(197,160,89,.35)] transition-all hover:-translate-y-0.5 ' +
    'active:scale-[0.98] disabled:opacity-70 disabled:hover:translate-y-0 disabled:cursor-not-allowed';

const QUIET_LINK_CLASS = 'text-sand/70 transition-colors hover:text-sand';

export const Auth: React.FC<AuthProps> = ({ defaultMode = 'login', postPurchase = false }) => {
    const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>(defaultMode);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [resetSent, setResetSent] = useState(false);
    const { login, signup, resetPassword, error } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);

    // Funnel impression: this screen sits between the pre-login sell and the
    // account (on Android, between the PURCHASE and the account) — without
    // this event that step is invisible on native, where GA4 has no automatic
    // page views. Fired once per mount with the mode the visitor landed in.
    useEffect(() => {
        trackEvent('auth_shown', { mode: defaultMode, postPurchase });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auth failures (email/password AND social — both surface through the
    // context's error string). Fired on each new error, with the mode so
    // sign-up friction is separable from sign-in friction.
    const lastTrackedError = useRef<string | null>(null);
    useEffect(() => {
        if (!error || error === lastTrackedError.current) return;
        lastTrackedError.current = error;
        trackEvent('auth_error', { mode, postPurchase });
    }, [error, mode, postPurchase]);

    const checkEmailTypo = useCallback((value: string) => {
        if (!value.includes('@')) {
            setEmailSuggestion(null);
            return;
        }
        const result = emailSpellChecker.run({ email: value });
        setEmailSuggestion(result?.full ?? null);
    }, []);

    const acceptSuggestion = useCallback(() => {
        if (emailSuggestion) {
            setEmail(emailSuggestion);
            setEmailSuggestion(null);
        }
    }, [emailSuggestion]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (mode === 'forgot') {
                await resetPassword(email);
                setResetSent(true);
            } else if (mode === 'login') {
                await login(email, password);
            } else {
                await signup(email, password);
            }
        } catch (err: unknown) {
            const authErr = err as { code?: string };
            if (authErr?.code === 'auth/user-not-found' && mode === 'login') {
                setMode('signup');
            }
            logError(err, { context: 'auth_failed' });
        } finally {
            setIsLoading(false);
        }
    };

    const emailField = (
        <div>
            <label htmlFor="email" className={LABEL_CLASS}>Email</label>
            <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailSuggestion(null); }}
                onBlur={(e) => checkEmailTypo(e.target.value)}
                className={FIELD_CLASS}
                placeholder="you@example.com"
            />
            {emailSuggestion && (
                <button type="button" onClick={acceptSuggestion} className="mt-1.5 text-xs text-stream hover:underline">
                    Did you mean <strong>{emailSuggestion}</strong>?
                </button>
            )}
        </div>
    );

    return (
        <div className="relative flex min-h-dvh-safe flex-col overflow-hidden bg-growth font-sans">
            {/* Soft color blobs — the funnel's signature dark treatment
                (ValueOnboarding uses the same two). */}
            <div
                aria-hidden
                className="absolute z-0 rounded-full blur-[60px]"
                style={{ width: 300, height: 300, background: 'rgba(197,160,89,.08)', top: '18%', left: '-12%' }}
            />
            <div
                aria-hidden
                className="absolute z-0 rounded-full blur-[60px]"
                style={{ width: 260, height: 260, background: 'rgba(81,107,148,.08)', bottom: '8%', right: '-12%' }}
            />

            <div className={`relative z-[2] flex flex-1 flex-col px-7 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] pt-[calc(env(safe-area-inset-top)+1.4rem)] ${FUNNEL_COLUMN}`}>
                {/* Chrome: wordmark, matching the carousel's header exactly. */}
                <div className="flex items-center justify-center">
                    <img src="/assets/logos/enough wordmark_beige.png" alt="enough." className="h-9 w-auto" />
                </div>

                <div className="flex flex-1 flex-col justify-center py-6">
                    <div className="w-full">
                        {/* Says what this screen is for, not what the product is
                            — the carousel already sold the product, and by here
                            the only open question is which door you're walking
                            through. Funnel headline SCALE, but set in font-serif
                            (Justin, 2026-07-21): the carousel and paywall
                            headlines are sans font-semibold, so this one is
                            deliberately its own thing. */}
                        <h1 className="mb-6 text-left text-[1.4rem] font-serif text-cloud">
                            {mode === 'signup'
                                ? 'Create your account'
                                : mode === 'forgot'
                                    ? 'Reset your password'
                                    : 'Sign in'}
                        </h1>

                        {/* This slot is reserved for ERRORS only (Justin,
                            2026-07-21) — no informational callouts above the
                            form; the post-purchase "save your membership"
                            notice used to live here and was cut. */}
                        {error && (
                            <div
                                className={`mb-4 rounded-xl border p-3 text-xs ${
                                    error.includes('No account found')
                                        ? 'border-stream/30 bg-stream/15 text-sand'
                                        : 'border-wildfire/30 bg-wildfire/15 text-sand'
                                }`}
                            >
                                {error}
                            </div>
                        )}

                        {mode === 'forgot' && resetSent ? (
                            <div className="space-y-4 text-center">
                                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sand/10">
                                    <Icons.Mail className="h-6 w-6 text-foliage" />
                                </div>
                                <p className="text-sm text-sand/80">
                                    We sent a password reset link to <strong className="text-sand">{email}</strong>. Check your inbox and follow the link to reset your password.
                                </p>
                                <button
                                    onClick={() => { setMode('login'); setResetSent(false); }}
                                    className={`text-sm font-medium ${QUIET_LINK_CLASS}`}
                                >
                                    Back to Sign In
                                </button>
                            </div>
                        ) : mode === 'forgot' ? (
                            <>
                                <p className="mb-4 text-center text-sm text-sand/70">
                                    Enter your email and we'll send you a link to reset your password.
                                </p>
                                <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
                                    {emailField}
                                    <button type="submit" disabled={isLoading} className={PRIMARY_CTA_CLASS}>
                                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </form>
                                <div className="mt-6 text-center">
                                    <button
                                        onClick={() => setMode('login')}
                                        className={`text-sm font-medium ${QUIET_LINK_CLASS}`}
                                    >
                                        Back to Sign In
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Social first (Justin, 2026-07-21): one tap
                                    beats typing an email and inventing a
                                    password, and it's the path most people
                                    take. Vendor-branded on purpose — see the
                                    note at the top of this file. */}
                                <SocialAuthButtons layout="stacked" />

                                <div className="my-6 flex items-center gap-4">
                                    <div className="h-px flex-1 bg-sand/15"></div>
                                    <span className="text-[11px] font-medium uppercase tracking-wide text-sand/50">
                                        OR USE EMAIL
                                    </span>
                                    <div className="h-px flex-1 bg-sand/15"></div>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
                                    {emailField}
                                    <div>
                                        <label htmlFor="password" className={LABEL_CLASS}>Password</label>
                                        <input
                                            id="password"
                                            name="password"
                                            type="password"
                                            autoComplete="current-password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={FIELD_CLASS}
                                            placeholder="••••••••"
                                        />
                                    </div>

                                    {mode === 'login' && (
                                        <div className="text-right">
                                            <button
                                                type="button"
                                                onClick={() => setMode('forgot')}
                                                className="text-xs text-sand/55 transition-colors hover:text-sand"
                                            >
                                                Forgot password?
                                            </button>
                                        </div>
                                    )}

                                    <button type="submit" disabled={isLoading} className={PRIMARY_CTA_CLASS}>
                                        {isLoading ? 'Processing...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
                                    </button>
                                </form>

                                <div className="mt-8 text-center">
                                    <button
                                        onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                                        className={`text-sm font-medium ${QUIET_LINK_CLASS}`}
                                    >
                                        {mode === 'login' ? "Don't have an account? Sign Up" : "Already have an account? Sign In"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
